import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { IconSelect } from "@/components/admin/IconSelect";
import { MediaPickerModal } from "@/components/media/MediaPickerModal";
import { Button } from "@/components/ui/Button";
import { useAboutPage, useUpdateAboutPage } from "@/features/about/hooks";
import type { AboutFaq, AboutPageInput, AboutSpecialty, AboutTimelineEntry } from "@/features/about/types";

const emptyForm: AboutPageInput = {
  pageTitle: "About Kalaco",
  subtitle: "",
  profileImageUrl: "",
  biography: "",
  specialties: [],
  timeline: [],
  faqs: [],
  ctaHeadline: "",
  ctaDescription: "",
  ctaButtonText: "",
  ctaButtonUrl: "",
};

export function AboutPageAdmin() {
  const about = useAboutPage();
  const updateAbout = useUpdateAboutPage();
  const [form, setForm] = useState<AboutPageInput>(emptyForm);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!about.data) return;
    setForm({
      pageTitle: about.data.pageTitle,
      subtitle: about.data.subtitle ?? "",
      profileImageUrl: about.data.profileImageUrl ?? "",
      biography: about.data.biography ?? "",
      specialties: about.data.specialties.map(stripId),
      timeline: about.data.timeline.map(stripId),
      faqs: about.data.faqs.map(stripId),
      ctaHeadline: about.data.ctaHeadline ?? "",
      ctaDescription: about.data.ctaDescription ?? "",
      ctaButtonText: about.data.ctaButtonText ?? "",
      ctaButtonUrl: about.data.ctaButtonUrl ?? "",
    });
  }, [about.data]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    updateAbout.mutate(form, {
      onSuccess: () => setMessage({ tone: "success", text: "About page saved." }),
      onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to save about page." }),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader title="About Page" description="Manage the public About page hero, biography, specialties, timeline, FAQ, and CTA." />
      {about.isLoading ? (
        <div className="rounded-md border border-cyan-300/15 bg-black/25 p-6 text-sm text-content-secondary">Loading about content...</div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section title="Hero">
            <TextField label="Page Title" value={form.pageTitle} onChange={(value) => setForm((current) => ({ ...current, pageTitle: value }))} required />
            <TextArea label="Subtitle" value={form.subtitle ?? ""} onChange={(value) => setForm((current) => ({ ...current, subtitle: value }))} />
            <MediaUrlField value={form.profileImageUrl ?? ""} onChange={(value) => setForm((current) => ({ ...current, profileImageUrl: value }))} onChoose={() => setMediaOpen(true)} />
          </Section>

          <Section title="Biography">
            <TextArea label="Rich Text / Markdown Biography" rows={12} value={form.biography} onChange={(value) => setForm((current) => ({ ...current, biography: value }))} />
          </Section>

          <RepeatableSection
            title="Specialties"
            addLabel="Add Specialty"
            onAdd={() => setForm((current) => ({ ...current, specialties: [...current.specialties, { icon: "", title: "", description: "", sortOrder: current.specialties.length }] }))}
          >
            {form.specialties.map((item, index) => (
              <SpecialtyEditor key={index} item={item} onChange={(next) => updateArray("specialties", index, next)} onRemove={() => removeArray("specialties", index)} />
            ))}
          </RepeatableSection>

          <RepeatableSection
            title="Experience Timeline"
            addLabel="Add Timeline Entry"
            onAdd={() => setForm((current) => ({ ...current, timeline: [...current.timeline, { year: "", title: "", description: "", sortOrder: current.timeline.length }] }))}
          >
            {form.timeline.map((item, index) => (
              <TimelineEditor key={index} item={item} onChange={(next) => updateArray("timeline", index, next)} onRemove={() => removeArray("timeline", index)} />
            ))}
          </RepeatableSection>

          <RepeatableSection
            title="FAQ"
            addLabel="Add FAQ"
            onAdd={() => setForm((current) => ({ ...current, faqs: [...current.faqs, { question: "", answer: "", sortOrder: current.faqs.length }] }))}
          >
            {form.faqs.map((item, index) => (
              <FaqEditor key={index} item={item} onChange={(next) => updateArray("faqs", index, next)} onRemove={() => removeArray("faqs", index)} />
            ))}
          </RepeatableSection>

          <Section title="Call To Action">
            <TextField label="Headline" value={form.ctaHeadline ?? ""} onChange={(value) => setForm((current) => ({ ...current, ctaHeadline: value }))} />
            <TextArea label="Description" value={form.ctaDescription ?? ""} onChange={(value) => setForm((current) => ({ ...current, ctaDescription: value }))} />
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label="Button Text" value={form.ctaButtonText ?? ""} onChange={(value) => setForm((current) => ({ ...current, ctaButtonText: value }))} />
              <TextField label="Button URL" value={form.ctaButtonUrl ?? ""} onChange={(value) => setForm((current) => ({ ...current, ctaButtonUrl: value }))} />
            </div>
          </Section>

          {message ? <p className={message.tone === "success" ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100" : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"}>{message.text}</p> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={updateAbout.isPending}>{updateAbout.isPending ? "Saving..." : "Save About Page"}</Button>
          </div>
        </form>
      )}

      <MediaPickerModal
        isOpen={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(items) => {
          const item = items[0];
          if (item) setForm((current) => ({ ...current, profileImageUrl: item.url }));
        }}
      />
    </div>
  );

  function updateArray<K extends "specialties" | "timeline" | "faqs">(key: K, index: number, value: AboutPageInput[K][number]) {
    setForm((current) => ({
      ...current,
      [key]: current[key].map((item, itemIndex) => itemIndex === index ? value : item),
    }));
  }

  function removeArray<K extends "specialties" | "timeline" | "faqs">(key: K, index: number) {
    setForm((current) => ({
      ...current,
      [key]: current[key].filter((_item, itemIndex) => itemIndex !== index),
    }));
  }
}

function stripId<T extends { id?: string }>(item: T): Omit<T, "id"> {
  const { id: _id, ...rest } = item;
  return rest;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 rounded-md border border-cyan-300/15 bg-black/25 p-4">
      <h3 className="font-display text-lg font-semibold uppercase text-white">{title}</h3>
      {children}
    </section>
  );
}

function RepeatableSection({ title, addLabel, onAdd, children }: { title: string; addLabel: string; onAdd: () => void; children: ReactNode }) {
  return (
    <Section title={title}>
      <div className="space-y-4">{children}</div>
      <Button type="button" variant="secondary" onClick={onAdd}>{addLabel}</Button>
    </Section>
  );
}

function SpecialtyEditor({ item, onChange, onRemove }: { item: AboutSpecialty; onChange: (item: AboutSpecialty) => void; onRemove: () => void }) {
  return (
    <div className="space-y-3 rounded-md border border-cyan-300/10 bg-black/25 p-3">
      <div className="grid gap-3 md:grid-cols-[8rem_1fr_8rem]">
        <IconSelect value={item.icon ?? ""} onChange={(value) => onChange({ ...item, icon: value })} />
        <TextField label="Title" value={item.title} onChange={(value) => onChange({ ...item, title: value })} />
        <NumberField label="Sort" value={item.sortOrder} onChange={(value) => onChange({ ...item, sortOrder: value })} />
      </div>
      <TextArea label="Description" value={item.description} onChange={(value) => onChange({ ...item, description: value })} />
      <Button type="button" size="sm" variant="danger" onClick={onRemove}>Remove</Button>
    </div>
  );
}

function TimelineEditor({ item, onChange, onRemove }: { item: AboutTimelineEntry; onChange: (item: AboutTimelineEntry) => void; onRemove: () => void }) {
  return (
    <div className="space-y-3 rounded-md border border-cyan-300/10 bg-black/25 p-3">
      <div className="grid gap-3 md:grid-cols-[8rem_1fr_8rem]">
        <TextField label="Year" value={item.year} onChange={(value) => onChange({ ...item, year: value })} />
        <TextField label="Title" value={item.title} onChange={(value) => onChange({ ...item, title: value })} />
        <NumberField label="Sort" value={item.sortOrder} onChange={(value) => onChange({ ...item, sortOrder: value })} />
      </div>
      <TextArea label="Description" value={item.description} onChange={(value) => onChange({ ...item, description: value })} />
      <Button type="button" size="sm" variant="danger" onClick={onRemove}>Remove</Button>
    </div>
  );
}

function FaqEditor({ item, onChange, onRemove }: { item: AboutFaq; onChange: (item: AboutFaq) => void; onRemove: () => void }) {
  return (
    <div className="space-y-3 rounded-md border border-cyan-300/10 bg-black/25 p-3">
      <div className="grid gap-3 md:grid-cols-[1fr_8rem]">
        <TextField label="Question" value={item.question} onChange={(value) => onChange({ ...item, question: value })} />
        <NumberField label="Sort" value={item.sortOrder} onChange={(value) => onChange({ ...item, sortOrder: value })} />
      </div>
      <TextArea label="Answer" value={item.answer} onChange={(value) => onChange({ ...item, answer: value })} />
      <Button type="button" size="sm" variant="danger" onClick={onRemove}>Remove</Button>
    </div>
  );
}

function MediaUrlField({ value, onChange, onChoose }: { value: string; onChange: (value: string) => void; onChoose: () => void }) {
  return (
    <div>
      <TextField label="Profile Image URL" value={value} onChange={onChange} />
      <Button className="mt-2" type="button" size="sm" variant="secondary" onClick={onChoose}>Choose from Media</Button>
    </div>
  );
}

function TextField({ label, value, onChange, required }: { label: string; value: string; onChange: (value: string) => void; required?: boolean }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)} required={required} />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 4 }: { label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <textarea className="mt-2 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white" rows={rows} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
